import { Box, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import radioButtonOffOutline from '@iconify/icons-eva/radio-button-off-outline';
import infoOutline from '@iconify/icons-eva/info-outline';

import { PASSWORD_REQUIREMENTS } from '../../schemas/password-policy';

interface PasswordRequirementsProps {
  /** Current value of the password field, used to evaluate each rule live. */
  password: string;
}

/**
 * Live checklist of the NIST 800-63B password requirements (BreachLock C.1.5).
 *
 * Each client-checkable rule turns green as the typed password satisfies it.
 * The breach rule is informational — it can only be confirmed by the server on
 * submit (see `PASSWORD_REQUIREMENTS` / `validatePassword`). The server remains
 * the source of truth; this is purely a UX hint.
 *
 * Colours are passed as MUI palette *tokens* (e.g. `success.main`) via `sx`
 * rather than read off `useTheme().palette.*` directly: the app nests multiple
 * ThemeProviders and the innermost theme (theme/palette.ts) does not define
 * every custom key, so direct object access (`palette.grayMedium.main`) throws.
 * Token resolution degrades gracefully; icons inherit the resolved colour via
 * `currentColor`.
 */
export const PasswordRequirements = ({ password }: PasswordRequirementsProps) => {
  // Before the user types anything, show every rule in a neutral "pending"
  // state rather than evaluating them — an empty field shouldn't present the
  // rules as already failed. (This is also why we don't change isMet('') to
  // return true: the rule logic stays honest; only the presentation defers.)
  const pristine = password.length === 0;

  return (
    <Box sx={{ mt: 1, mb: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        Your password must:
      </Typography>
      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.5 }}>
        {PASSWORD_REQUIREMENTS.map((requirement) => {
          const met = !pristine && requirement.isMet(password);

          // serverOnly (breach) is always informational; until the field has a
          // value, every checkable rule reads neutral; after that, met → green,
          // unmet → muted. All tokens exist on the innermost theme.
          const icon = met
            ? checkmarkCircle2Fill
            : requirement.serverOnly
            ? infoOutline
            : radioButtonOffOutline;

          const color =
            requirement.serverOnly || pristine
              ? 'text.secondary'
              : met
              ? 'success.main'
              : 'text.disabled';

          return (
            <Box
              key={requirement.key}
              component="li"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, color }}
            >
              <Box sx={{ display: 'inline-flex', color: 'inherit' }}>
                <Icon icon={icon} color="currentColor" width={18} height={18} />
              </Box>
              <Typography variant="caption" sx={{ color: 'inherit' }}>
                {requirement.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default PasswordRequirements;
